import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireAdmin } from '@/components/auth/RequireAdmin';
import { ProjectProvider } from '@/contexts/ProjectContext';
import { WorkAreaProvider } from '@/contexts/WorkAreaContext';
import { queryClient } from '@/lib/query-client';
import { DashboardPage } from '@/routes/DashboardPage';
import { ChatPage } from '@/routes/ChatPage';
import { WorkflowsPage } from '@/routes/WorkflowsPage';
import { WorkflowExecutionPage } from '@/routes/WorkflowExecutionPage';
import { WorkflowBuilderPage } from '@/routes/WorkflowBuilderPage';
import { WorkAreasPage } from '@/routes/WorkAreasPage';
import { WorkAreaWorkbenchPage } from '@/routes/WorkAreaWorkbenchPage';
import { ArtifactsPage } from '@/routes/ArtifactsPage';
import { ArtifactDetailPage } from '@/routes/ArtifactDetailPage';
import { AccountPage } from '@/routes/AccountPage';
import { AdminPage } from '@/routes/AdminPage';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught rendering error', {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-background p-8">
          <div className="max-w-md text-center">
            <h1 className="mb-2 text-xl font-semibold text-text-primary">Something went wrong</h1>
            <p className="mb-4 text-sm text-text-secondary">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
            <button
              onClick={(): void => {
                window.location.reload();
              }}
              className="rounded-md bg-surface-elevated px-4 py-2 text-sm text-text-primary hover:bg-surface-elevated"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AdminRoute(): React.ReactElement {
  return (
    <RequireAdmin>
      <AdminPage />
    </RequireAdmin>
  );
}

const router = createBrowserRouter([
  {
    element: (
      <RequireAuth>
        <ProjectProvider>
          <WorkAreaProvider>
            <Layout />
          </WorkAreaProvider>
        </ProjectProvider>
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <Navigate to="/chat" replace /> },
      { path: '/chat', element: <ChatPage /> },
      { path: '/chat/*', element: <ChatPage /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/workflows', element: <WorkflowsPage /> },
      { path: '/workflows/builder', element: <WorkflowBuilderPage /> },
      { path: '/workflows/runs/:runId', element: <WorkflowExecutionPage /> },
      { path: '/workflows/runs', element: <Navigate to="/workflows" replace /> },
      { path: '/work-areas', element: <WorkAreasPage /> },
      { path: '/work-areas/:workAreaId/workbench', element: <WorkAreaWorkbenchPage /> },
      { path: '/artifacts', element: <ArtifactsPage /> },
      { path: '/artifacts/:artifactId', element: <ArtifactDetailPage /> },
      { path: '/account', element: <AccountPage /> },
      { path: '/admin', element: <AdminRoute /> },
      { path: '/admin/members', element: <AdminRoute /> },
      { path: '/admin/audit', element: <AdminRoute /> },
      { path: '/admin/provider-keys', element: <AdminRoute /> },
      { path: '/admin/assistant-defaults', element: <AdminRoute /> },
      { path: '/admin/operations', element: <AdminRoute /> },
      { path: '/admin/resources', element: <AdminRoute /> },
      { path: '/admin/resources/commands', element: <AdminRoute /> },
      { path: '/admin/resources/commands/:name', element: <AdminRoute /> },
      { path: '/admin/resources/scripts', element: <AdminRoute /> },
      { path: '/admin/resources/scripts/:name', element: <AdminRoute /> },
      { path: '/admin/resources/skills', element: <AdminRoute /> },
      { path: '/admin/resources/skills/:name', element: <AdminRoute /> },
      { path: '/admin/resources/mcp-configs', element: <AdminRoute /> },
      { path: '/admin/resources/mcp-configs/:name', element: <AdminRoute /> },
    ],
  },
]);

export function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
